import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Loader2, Save, Trash2, Plus, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { validateSepaIban } from '../../../utils/sepa';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'da_classificare', label: 'Da classificare', color: 'bg-amber-100 text-amber-800' },
  { value: 'proprietario', label: 'Proprietario', color: 'bg-blue-100 text-blue-800' },
  { value: 'fornitore', label: 'Fornitore', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'partner', label: 'Partner', color: 'bg-purple-100 text-purple-800' },
  { value: 'comune', label: 'Comune', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'altro', label: 'Altro', color: 'bg-slate-100 text-slate-700' },
];

const fmtEur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

export default function PaymentContactEditModal({ contact, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: contact?.name || '',
    iban: contact?.iban || '',
    category: contact?.category || 'da_classificare',
    bic: contact?.bic || '',
    codice_fiscale: contact?.codice_fiscale || '',
    address: contact?.address || '',
    city: contact?.city || '',
    cap: contact?.cap || '',
    province: contact?.province || '',
    country: contact?.country || '',
    notes: contact?.notes || '',
    aliases: contact?.aliases || [],
  });
  const [newAlias, setNewAlias] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const token = localStorage.getItem('hw_token');
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const addAlias = () => {
    const a = newAlias.trim();
    if (!a) return;
    if (form.aliases.some(x => x.toLowerCase() === a.toLowerCase())) {
      toast.error('Alias già presente');
      return;
    }
    set('aliases', [...form.aliases, a]);
    setNewAlias('');
  };
  const removeAlias = (i) => set('aliases', form.aliases.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Nome obbligatorio'); return; }
    const ibanN = (form.iban || '').toUpperCase().replace(/\s/g, '');
    if (ibanN) {
      const validation = validateSepaIban(ibanN);
      if (validation.code === 'INVALID_IBAN') { toast.error(validation.reason); return; }
      if (validation.code === 'EXTRA_SEPA_COUNTRY') toast.warning(validation.reason, { duration: 8000 });
    }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/payment-contacts/${contact.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, iban: ibanN }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${r.status}`);
      }
      toast.success('Contatto aggiornato');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(`Errore: ${e.message}`);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Eliminare definitivamente il contatto "${contact.name}"? Lo storico pagamenti rimarrà nei batch.`)) return;
    setDeleting(true);
    try {
      const r = await fetch(`${API_URL}/api/payment-contacts/${contact.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Contatto eliminato');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(`Errore: ${e.message}`);
    }
    setDeleting(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="contact-edit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Modifica contatto rubrica
            <Badge className={(CATEGORIES.find(c => c.value === form.category) || CATEGORIES[0]).color + ' ml-2'}>
              {(CATEGORIES.find(c => c.value === form.category) || CATEGORIES[0]).label}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Stats badge */}
          <Card className="p-3 bg-slate-50">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="text-slate-500">N° pagamenti</div>
                <div className="font-semibold text-base">{contact?.transactions_count || 0}</div>
              </div>
              <div>
                <div className="text-slate-500">Totale storico</div>
                <div className="font-semibold text-base">{fmtEur(contact?.total_invoiced)}</div>
              </div>
              <div>
                <div className="text-slate-500">Ultimo importo</div>
                <div className="font-semibold text-base">{fmtEur(contact?.last_amount)}</div>
              </div>
              <div>
                <div className="text-slate-500">Origini</div>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {((contact?.sources && contact.sources.length) ? contact.sources : (contact?.source ? [contact.source] : [])).map(s => (
                    <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* Form principali */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-slate-700">Nome / Ragione sociale *</label>
              <Input
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                data-testid="contact-name-input"
                placeholder="es. Comune di Genova uff. IDS"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">IBAN</label>
              <Input
                value={form.iban}
                onChange={(e) => set('iban', e.target.value.toUpperCase())}
                data-testid="contact-iban-input"
                placeholder="es. IT60X0542811101000000123456"
                className="font-mono"
              />
              {form.iban && form.iban.length > 4 && (
                <p className="text-[10px] text-slate-400 mt-1">{form.iban.length} caratteri · XML disponibili per IBAN validi di Paesi SEPA</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Categoria *</label>
              <Select value={form.category} onValueChange={(v) => set('category', v)}>
                <SelectTrigger data-testid="contact-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">BIC / SWIFT</label>
              <Input value={form.bic} onChange={(e) => set('bic', e.target.value.toUpperCase())} className="font-mono" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Codice Fiscale / P.IVA</label>
              <Input value={form.codice_fiscale} onChange={(e) => set('codice_fiscale', e.target.value.toUpperCase())} className="font-mono" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-slate-700">Indirizzo</label>
              <Input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Via..." />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">CAP</label>
              <Input value={form.cap} onChange={(e) => set('cap', e.target.value)} maxLength={5} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Città</label>
              <Input value={form.city} onChange={(e) => set('city', e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Provincia</label>
              <Input value={form.province} onChange={(e) => set('province', e.target.value.toUpperCase())} maxLength={2} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Paese</label>
              <Input value={form.country} onChange={(e) => set('country', e.target.value.toUpperCase())} maxLength={2} placeholder="IT" />
            </div>
          </div>

          {/* Aliases */}
          <Card className="p-3">
            <label className="text-xs font-medium text-slate-700">Alias (altri nomi con cui appare negli XML)</label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {form.aliases.map((a, i) => (
                <Badge key={i} className="bg-blue-50 text-blue-800 border border-blue-200 pr-1 py-1">
                  {a}
                  <button onClick={() => removeAlias(i)} className="ml-1 hover:bg-blue-200 rounded p-0.5">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {form.aliases.length === 0 && <span className="text-xs text-slate-400 italic">Nessun alias</span>}
            </div>
            <div className="flex gap-2 mt-2">
              <Input
                value={newAlias}
                onChange={(e) => setNewAlias(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addAlias())}
                placeholder="Aggiungi un alias (es. nome breve)"
                className="h-8 text-sm"
                data-testid="new-alias-input"
              />
              <Button size="sm" variant="outline" onClick={addAlias}><Plus className="h-3 w-3" /></Button>
            </div>
          </Card>

          {/* Notes */}
          <div>
            <label className="text-xs font-medium text-slate-700">Note interne</label>
            <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t">
            <Button variant="ghost" size="sm" onClick={handleDelete} disabled={deleting} className="text-red-600 hover:bg-red-50">
              {deleting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Trash2 className="h-3 w-3 mr-1" />}
              Elimina contatto
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Annulla</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-contact-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Salva modifiche
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
